import { useEffect, useState } from 'react'

import { SetState, Workspace } from 'types'
import { pluralize } from 'utils'
import { useGoogleContacts } from 'hooks/useGoogleContacts'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { ReactComponent as ShareIcon } from 'assets/icons/share.svg'
import { HeapEventType, trackEventInHeap } from 'utils/heapTracking'
import ImportUserRow, { GoogleContact } from './ImportUserRow'
import { EmptyState, LoadingState } from 'common/GoogleIntegration/GoogleStates'
import { Button } from 'components/Button/Button'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

import Styles from './InviteTeam.module.scss'
import { Modal } from 'almanac-ui/Modal'
import { ArrowLeft } from 'almanac-ui/icons/16'
import { Heading } from 'almanac-ui/Heading/Heading'
interface ImportEmailsProps {
  onClose: () => void
  setShowImport: SetState<boolean>
  workspace: Workspace
}

const ImportEmails: React.FC<React.PropsWithChildren<ImportEmailsProps>> = ({ onClose, setShowImport, workspace }) => {
  const { isMobile } = useResponsiveDesign()

  const contacts = useGoogleContacts()

  const [allSelected, setAllSelected] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<GoogleContact[]>([])
  const [contactsAreLoading, setContactsAreLoading] = useState<Boolean>(true)

  useEffect(() => {
    if (!contacts) return

    trackEventInHeap(HeapEventType.ImportContacts, {
      action: 'google_contact_import_triage_started',
      type: `${contacts.length} Contacts Fetched from Google`,
    })

    setSelectedUsers(contacts)
    setContactsAreLoading(false)
  }, [contacts])

  useEffect(() => {
    if (!contacts) return

    setAllSelected(contacts.length === selectedUsers.length)
  }, [selectedUsers.length, contacts])

  const goBack = () => {
    setShowImport(false)
  }

  const selectAll = () => {
    if (!contacts) return

    allSelected ? setSelectedUsers([]) : setSelectedUsers(contacts)
  }

  const inviteMembers = async () => {
    if (!selectedUsers.length) return

    if (!workspace) {
      showToastMessage('Unable to invite team members', 'error')
      return
    }

    setIsProcessing(true)

    const failedInvitations: string[] = []

    trackEventInHeap(HeapEventType.ImportContacts, {
      action: 'google_contact_import_finished',
      type: `${selectedUsers.length} Contacts Imported from Google`,
    })

    await Promise.all(
      selectedUsers.map(async ({ email }) => {
        const res = await client.addMember({
          email: email,
          role: 'User',
          workspaceKey: workspace.key,
          inviteReason: 'user_import_google',
        })

        if (res.type !== SUCCESS_RESPONSE_TYPE) {
          failedInvitations.push(email)
        }
      }),
    )

    setIsProcessing(false)

    failedInvitations.length === 0
      ? showToastMessage('Your teammates were invited!', 'success')
      : showToastMessage(`Couldn't invite the following users: ${failedInvitations.join(', ')}. Try again.`, 'error')

    onClose()
  }

  const Footer = () => {
    return (
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          {contacts && (
            <Modal.FooterButton variant="tertiary" size="lg" onPress={selectAll}>
              {allSelected
                ? `Unselect all ${contacts.length} ${pluralize('Contact', contacts.length)}`
                : `Select all ${contacts.length} ${pluralize('Contact', contacts.length)}`}
            </Modal.FooterButton>
          )}
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton
            size="lg"
            onPress={inviteMembers}
            isDisabled={isProcessing || !selectedUsers.length}
            frontIcon={ShareIcon}
            iconClassName={Styles.shareIcon}
          >
            {`Invite ${selectedUsers.length} ${pluralize('Member', selectedUsers.length)}`}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    )
  }

  const userIncluded = (u: GoogleContact) => {
    return !!selectedUsers.filter((su) => su.email === u.email).length
  }

  const onCheck = (user: GoogleContact) => {
    if (userIncluded(user)) {
      setSelectedUsers(selectedUsers.filter((u) => u.email !== user.email))
    } else {
      setSelectedUsers(selectedUsers.concat(user))
    }
  }

  const userRows = () => {
    if (contactsAreLoading) {
      return <LoadingState />
    }

    if (!contacts || !contacts.length) {
      return <EmptyState text={'No contacts found.'} subtext={'Try another google account.'} />
    }

    return contacts.map((u: GoogleContact) => (
      <ImportUserRow
        key={(u.name + u.email).split(' ').join('')}
        user={u}
        selected={userIncluded(u)}
        onCheck={onCheck}
      />
    ))
  }

  return (
    <>
      <div className={Styles.googleContactsHeader}>
        {!isMobile && (
          <Button variant="tertiary" size="lg" onPress={goBack}>
            <ArrowLeft aria-label="Go Back" />
          </Button>
        )}
        <Heading level={2}>Google Contacts</Heading>
      </div>
      <Modal.Content>{userRows()}</Modal.Content>
      <Footer />
    </>
  )
}

export default ImportEmails
